// Report timeframe model + SQL template resolution.
//
// A panel opts into time-bounding by embedding placeholders in its SQL:
//
//   {{since:<N><s|m|h>}}  → lower cutoff, µs since epoch. With no override
//                           it resolves to now − N units (the panel's own
//                           default window); a report-level timeframe
//                           override replaces it.
//   {{until}}             → upper cutoff, µs since epoch. Resolves to `now`
//                           normally, or to the zoom window's end when an
//                           absolute range is active. Needed so a zoomed
//                           refetch is bounded on BOTH ends (`ORDER BY time
//                           DESC LIMIT N` would otherwise return only the
//                           newest rows, outside the zoom window).
//   {{bucket}}           → literal integer µs width for time_bucket(time, …),
//                           sized so the visible window holds ~TARGET_POINTS
//                           points: max(1, floor(windowUs / TARGET_POINTS)).
//                           Window follows the active range (see windowUsFor).
//
// The presence of {{since: in any panel's SQL is what makes a report
// "timeframe-aware" — no separate saved setting. Ranges:
//
//   null                              → every panel uses its own default
//   { kind:'relative', seconds }      → preset ("Last 15m", "Last 1h", …)
//   { kind:'absolute', fromUs, toUs } → mouse/touch zoom selection

export const SINCE_RE = /\{\{since:(\d+)([smh])\}\}/g;
const UNTIL_RE = /\{\{until\}\}/g;
const BUCKET_RE = /\{\{bucket\}\}/g;
// First {{since:N<unit>}} only — used to size a null-range panel's own window.
const FIRST_SINCE_RE = /\{\{since:(\d+)([smh])\}\}/;

// A line chart targets ~this many points across its window; the {{bucket}}
// width is windowUs / TARGET_POINTS so a wider window buckets more coarsely.
export const TARGET_POINTS = 400;

// {{since:}} unit → seconds.
function sinceUnitSeconds(unit) {
  return unit === 's' ? 1 : unit === 'm' ? 60 : 3600;
}

export const TIME_PRESETS = [
  { label: '5m',  seconds: 5 * 60 },
  { label: '15m', seconds: 15 * 60 },
  { label: '30m', seconds: 30 * 60 },
  { label: '1h',  seconds: 3600 },
  { label: '3h',  seconds: 3 * 3600 },
  { label: '6h',  seconds: 6 * 3600 },
  { label: '12h', seconds: 12 * 3600 },
  { label: '24h', seconds: 24 * 3600 },
  { label: '7d',  seconds: 7 * 24 * 3600 },
];

export function relativeRange(seconds) {
  return { kind: 'relative', seconds };
}

// Normalizes a reversed selection so callers can pass drag endpoints as-is.
export function absoluteRange(fromUs, toUs) {
  return fromUs <= toUs
    ? { kind: 'absolute', fromUs, toUs }
    : { kind: 'absolute', fromUs: toUs, toUs: fromUs };
}

export function sqlUsesTimeframe(sql) {
  return typeof sql === 'string' && sql.includes('{{since:');
}

export function reportHasTimeframe(report) {
  return !!report?.panels?.some(p => sqlUsesTimeframe(p.sql));
}

// Resolve template placeholders to absolute µs-since-epoch cutoffs.
// `nowMs` is injectable for tests; call sites omit it. Recomputed on every
// fetch so relative windows slide forward with wall-clock.
export function resolveTemplateSql(sql, range = null, nowMs = Date.now()) {
  if (!sql) return sql;
  const nowUs = nowMs * 1000;
  let sinceUs; // per-match when null
  let untilUs = nowUs;
  if (range?.kind === 'relative') {
    sinceUs = nowUs - range.seconds * 1_000_000;
  } else if (range?.kind === 'absolute') {
    sinceUs = range.fromUs;
    untilUs = range.toUs;
  }
  const bucketUs = Math.max(1, Math.floor(windowUsFor(sql, range) / TARGET_POINTS));
  return sql
    .replace(SINCE_RE, (_, n, unit) => {
      if (sinceUs != null) return String(sinceUs);
      return String(nowUs - Number(n) * sinceUnitSeconds(unit) * 1_000_000);
    })
    .replace(UNTIL_RE, String(untilUs))
    .replace(BUCKET_RE, String(bucketUs));
}

// The window a {{bucket}} spans, in µs, by range branch:
//   relative → range.seconds; absolute → toUs − fromUs; null → the panel's
//   own first {{since:N<unit>}} literal. Edge case: null range and no
//   {{since:}} in the SQL → a safe 15m default (matches the common panel
//   default) so a stray {{bucket}} still resolves to a sane width.
function windowUsFor(sql, range) {
  if (range?.kind === 'relative') return range.seconds * 1_000_000;
  if (range?.kind === 'absolute') return range.toUs - range.fromUs;
  const m = sql.match(FIRST_SINCE_RE);
  if (m) return Number(m[1]) * sinceUnitSeconds(m[2]) * 1_000_000;
  return 15 * 60 * 1_000_000;
}

function fmtClock(us) {
  const d = new Date(us / 1000);
  return Number.isNaN(d.getTime()) ? String(us) : d.toLocaleTimeString();
}

function fmtDuration(seconds) {
  if (seconds % 86400 === 0) return (seconds / 86400) + 'd';
  if (seconds % 3600 === 0)  return (seconds / 3600) + 'h';
  if (seconds % 60 === 0)    return (seconds / 60) + 'm';
  return seconds + 's';
}

export function describeRange(range) {
  if (!range) return 'Panel default';
  if (range.kind === 'relative') return `Last ${fmtDuration(range.seconds)}`;
  return `${fmtClock(range.fromUs)} – ${fmtClock(range.toUs)}`;
}
